import PropTypes from 'prop-types';
import { forwardRef } from 'react';
// @mui
import { Dialog, Slide, useMediaQuery } from '@mui/material';

// ----------------------------------------------------------------------

// Phone sized screens get a bottom sheet instead of a centred card: full width,
// rounded top, actions stacked and thumb reachable. Above sm nothing changes,
// the plain MUI Dialog renders exactly as before.

const SlideUp = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const sheetPaperSx = {
  m: 0,
  width: '100%',
  maxWidth: '100%',
  maxHeight: '92vh',
  borderRadius: '16px 16px 0 0',
  // Grab handle, so the sheet reads as a sheet and not a clipped dialog.
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 36,
    height: 4,
    borderRadius: 2,
    bgcolor: 'divider',
  },
  '& .MuiDialogTitle-root': {
    px: 2,
    pt: 2.5,
    pb: 1,
    '& .MuiAvatar-root': { width: 40, height: 40, mb: 1 },
    '& .MuiSvgIcon-root': { fontSize: 22 },
  },
  '& .MuiDialogContent-root': { px: 2, pb: 1 },
  '& .MuiDialogActions-root': {
    px: 2,
    pt: 1,
    // Bottom padding clears the iOS home indicator.
    pb: 'calc(16px + env(safe-area-inset-bottom))',
    // Reversed so the primary action, which is written last, sits on top.
    flexDirection: 'column-reverse',
    alignItems: 'stretch',
    gap: 1,
    '& > :not(:first-of-type)': { ml: 0 },
    '& .MuiButton-root': { width: '100%', py: 1.25 },
  },
};

ResponsiveDialog.propTypes = {
  children: PropTypes.node,
  PaperProps: PropTypes.object,
  sx: PropTypes.object,
};

export default function ResponsiveDialog({ children, PaperProps, sx, ...other }) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  if (!isMobile) {
    return (
      <Dialog PaperProps={PaperProps} sx={sx} {...other}>
        {children}
      </Dialog>
    );
  }

  return (
    <Dialog
      {...other}
      fullWidth
      TransitionComponent={SlideUp}
      // Bottom aligned rather than position: fixed, so the paper keeps MUI's
      // own scroll handling when the content is taller than the sheet.
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-end' }, ...sx }}
      PaperProps={{
        ...PaperProps,
        sx: { ...sheetPaperSx, ...PaperProps?.sx },
      }}
    >
      {children}
    </Dialog>
  );
}
